import {
  EXTENSION_MIME_MAP,
  GENERIC_BINARY_MIME_TYPE,
  KB_THRESHOLD,
  MB_THRESHOLD,
} from "../constants/chat";
import { encodePathSegment } from "./encode-path-segment";
import { getOwnValue } from "./get-own-value";

// NOTE: Shaped to match what callers already hold — a browser `File`, an
// `IAttachment`, or any `{ name, type }` — so no call site has to unpack fields
// just to ask this question. `type` is optional because some pickers omit it.
interface IResolveFileMimeTypeProps {
  name: string;
  type?: string | undefined;
}

interface IBuildChatFileDownloadPathProps {
  fileUuid: string;
}

export const formatBytesToMb = (bytes: number): string => {
  return `${(bytes / MB_THRESHOLD).toFixed(1)} MB`;
};

// NOTE: NSAI-5857 — a name with no dot has no extension. `split(".").pop()`
// returned the whole name instead, so "testfile" was looked up as the extension
// "testfile" and a file named just "pdf" passed for a PDF.
export const getFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return "";
  }

  return fileName.slice(lastDotIndex + 1).toLowerCase();
};

// NOTE: NSAI-5414 — the extension comes from a file name someone else may have
// chosen (a knowledge source, a dropped attachment), so "report.constructor"
// must not resolve to `Object` through the prototype chain: own entries only.
export const getMimeTypeFromFileName = (fileName: string): string => {
  const extension = getFileExtension(fileName);
  return (
    getOwnValue({ record: EXTENSION_MIME_MAP, key: extension }) ??
    GENERIC_BINARY_MIME_TYPE
  );
};

// NOTE: NSAI-3193 — a browser that has no MIME registered for an extension
// reports either "" or the generic "application/octet-stream". The latter is
// truthy, so `file.type || getMimeTypeFromFileName(...)` alone still leaks it
// through, and every downstream MIME check then fails: the chat capability gate
// rejects the attachment ("selected model does not support this file type") and
// the presign DTO's allowlist rejects the upload. Legacy Office binaries
// (.doc/.xls/.ppt) are the common victims. Prefer the extension-derived type
// whenever the reported one carries no information; unknown extensions still
// resolve to octet-stream, so nothing new is let through.
export const resolveFileMimeType = (
  props: IResolveFileMimeTypeProps,
): string => {
  const { name, type } = props;

  if (!type || type === GENERIC_BINARY_MIME_TYPE) {
    return getMimeTypeFromFileName(name);
  }

  return type;
};

export const formatFileSize = (bytes?: number): string => {
  if (!bytes) return "";
  if (bytes < KB_THRESHOLD) return `${bytes} B`;
  if (bytes < MB_THRESHOLD) return `${(bytes / KB_THRESHOLD).toFixed(1)} KB`;
  return formatBytesToMb(bytes);
};

// NOTE: NSAI-5312 — the per-file download a chat's files and every generated
// file (automation outputs included) are served from; the server checks the
// caller's ownership of that one file. Shared because mobile streams it to disk
// with its native downloader and must request exactly what ChatApi does.
export const buildChatFileDownloadPath = ({
  fileUuid,
}: IBuildChatFileDownloadPathProps): string =>
  `/chats/files/${encodePathSegment(fileUuid)}/download`;
